<!-- EditorToolbar.svelte -->
<script>
    // Change the prop name from tiptapEditor to editor to match what's passed from parent
    export let element;
    export let editor; // Changed from tiptapEditor to editor

    // Function to insert a link
    function insertLink() {
        if (!editor) return;
        editor.chain().focus().setLink({ href: "https://" }).run();
    }

    // Function to insert a code block with syntax highlighting
    // Properly handles multi-line selections
    // Function to insert a code block with syntax highlighting
    // Handles multi-line selections and prevents double-formatting
    function insertCodeBlock() {
        if (!editor) return;

        // Check if we're already in a code block
        if (editor.isActive("codeBlock")) {
            // If already in a code block, exit it
            editor.chain().focus().exitCode().run();
            return;
        }

        // Check if selection is within a parent code block
        const { from, to } = editor.state.selection;
        const isInCodeBlock = editor.isActive("codeBlock");

        // If we're inside a code block or trying to format existing code, just exit
        if (isInCodeBlock) {
            editor.chain().focus().exitCode().run();
            return;
        }

        // Handle selection
        if (from !== to) {
            // We have a selection
            try {
                // Get the selected text
                const selectedText = editor.state.doc.textBetween(
                    from,
                    to,
                    " ",
                    " ",
                );

                // Delete the current selection
                editor.chain().focus().deleteSelection().run();

                // Insert a code block with the selected content
                editor
                    .chain()
                    .focus()
                    .insertContent({
                        type: "codeBlock",
                        attrs: { language: "javascript" },
                        content: [{ type: "text", text: selectedText }],
                    })
                    .run();
            } catch (error) {
                console.error("Error handling code block selection:", error);
                // Fallback to the simpler method if something fails
                editor
                    .chain()
                    .focus()
                    .setCodeBlock({ language: "javascript" })
                    .run();
            }
        } else {
            // No selection, just create an empty code block
            editor
                .chain()
                .focus()
                .setCodeBlock({ language: "javascript" })
                .run();
        }
    }
</script>

<div bind:this={element} class="bubble-menu">
    <button
        on:click={() => editor?.chain().focus().toggleBold().run()}
        class:active={editor?.isActive("bold")}
        title="Bold"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
        </svg>
    </button>

    <button
        on:click={() => editor?.chain().focus().toggleItalic().run()}
        class:active={editor?.isActive("italic")}
        title="Italic"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <line x1="19" y1="4" x2="10" y2="4"></line>
            <line x1="14" y1="20" x2="5" y2="20"></line>
            <line x1="15" y1="4" x2="9" y2="20"></line>
        </svg>
    </button>

    <button
        on:click={() => editor?.chain().focus().toggleCode().run()}
        class:active={editor?.isActive("code")}
        title="Inline Code"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
    </button>

    <!-- New Code Block Button -->
    <button
        on:click={insertCodeBlock}
        class:active={editor?.isActive("codeBlock")}
        title="Code Block"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="code-block-icon"
        >
            <path
                d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"
            ></path>
            <path
                d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1"
            ></path>
        </svg>
    </button>

    <button on:click={insertLink} title="Insert Link">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <path
                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            ></path>
            <path
                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            ></path>
        </svg>
    </button>

    <!-- Task List button -->
    <button
        on:click={() => editor?.chain().focus().toggleTaskList().run()}
        class:active={editor?.isActive("taskList")}
        title="Task List"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <path d="M9 11l3 3L22 4"></path>
            <rect x="3" y="3" width="5" height="5" rx="1"></rect>
            <rect x="3" y="16" width="5" height="5" rx="1"></rect>
        </svg>
    </button>

    <!-- Hashtag button - to quickly insert a hashtag -->
    <button
        on:click={() => editor?.chain().focus().insertContent("#").run()}
        title="Insert Hashtag"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <line x1="4" y1="9" x2="20" y2="9"></line>
            <line x1="4" y1="15" x2="20" y2="15"></line>
            <line x1="10" y1="3" x2="8" y2="21"></line>
            <line x1="16" y1="3" x2="14" y2="21"></line>
        </svg>
    </button>
</div>

<style>
    .bubble-menu {
        display: flex;
        background-color: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 0.375rem;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 0.25rem;
        visibility: hidden; /* TipTap will handle showing this */
        align-items: center;
    }

    .bubble-menu button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        margin: 0 0.125rem;
        border: none;
        background: transparent;
        border-radius: 0.25rem;
        cursor: pointer;
    }

    .bubble-menu button:hover {
        background-color: #f3f4f6;
    }

    .bubble-menu button.active {
        color: #3b82f6;
        background-color: #eff6ff;
    }

    .bubble-menu button svg {
        width: 1.25rem;
        height: 1.25rem;
    }

    /* Special styling for the code block icon */
    .code-block-icon {
        width: 1.25rem;
        height: 1.25rem;
    }
</style>
